import { EMAIL_EVENTS_ORDER_BY_ID } from '@constants/emailEventsOrderById';

import { getAllChildrenIds } from '@helpers/getAllChildrenIds';

import { createStore } from '@store/createStore';

import { checkColumnValue } from '../helpers/checkColumnValue';

const getParentSubscriptions = (parentsLocIds, eventsKeys, subscriptions) => {
  if (!parentsLocIds.length) {
    return null;
  }

  let subscrParentId = null;
  let parentSubscriptions = null;

  for (const parentId of parentsLocIds) {
    if (subscriptions?.[eventsKeys[0]]?.[parentId]) {
      subscrParentId = parentId;

      break;
    }
  }

  if (subscrParentId) {
    eventsKeys.forEach((eventKey) => {
      if (!parentSubscriptions) {
        parentSubscriptions = {};
      }
      parentSubscriptions[eventKey] = {
        ...(subscriptions?.[eventKey]?.[subscrParentId]?.urgencies || { 1: 1, 2: 1, 3: 1 }),
      };
    });
  }

  return parentSubscriptions;
};

const useSubscriptionsStore = createStore((set) => ({
  allFacilitiesChecked: 0,
  subscrLocations: [],
  subscriptions: {},
  columnSubscriptions: {},
  eventsKeys: [],
  events: [],
  childrenIds: [],
  hasSubscriptionsChanges: false,
  actions: {
    setDefaultSubscriptions: ({ emailEventsDictionary, userSubscriptions, defaultLocs }) =>
      set(() => {
        const subscriptions = {};
        const events = [];
        const eventsKeys = [];
        const columnSubscriptions = { 1: 1, 2: 1, 3: 1 };

        (userSubscriptions || emailEventsDictionary || []).forEach((s) => {
          eventsKeys[EMAIL_EVENTS_ORDER_BY_ID[+s.event_id]] = +s.event_id;
          events[EMAIL_EVENTS_ORDER_BY_ID[+s.event_id]] = {
            event_id: +s.event_id,
            name: s.event?.name || s.name,
          };

          if (userSubscriptions.length) {
            subscriptions[s.event_id] = {};
            if (s.settings.length) {
              s.settings.forEach((setting) => {
                if (!subscriptions[s.event_id][setting.location_id]?.urgencies) {
                  subscriptions[s.event_id][setting.location_id] = { urgencies: {} };
                }
                subscriptions[s.event_id][setting.location_id].urgencies[setting.value] = setting.state;
              });
            }
          }

          const defaultLocSubscription = subscriptions[+s.event_id]?.[defaultLocs[0]];
          if (defaultLocSubscription) {
            if (!defaultLocSubscription.urgencies[1]) {
              columnSubscriptions[1] = 0;
            }
            if (!defaultLocSubscription.urgencies[2]) {
              columnSubscriptions[2] = 0;
            }
            if (!defaultLocSubscription.urgencies[3]) {
              columnSubscriptions[3] = 0;
            }
          } else {
            subscriptions[+s.event_id] = {
              ...subscriptions[+s.event_id],
            };
          }
        });

        return {
          subscriptions,
          columnSubscriptions,
          events,
          eventsKeys,
          subscrLocations: defaultLocs,
          allFacilitiesChecked: false,
          hasSubscriptionsChanges: false,
          childrenIds: [],
        };
      }),
    setAllFacilitiesChecked: (allFacilitiesChecked, facilities = []) =>
      set(({ eventsKeys }) => {
        if (!allFacilitiesChecked) {
          return { allFacilitiesChecked, subscrLocations: [] };
        }

        const subscriptions = {};
        const allCheckedByFacilities = {};

        facilities.forEach((facility) => {
          allCheckedByFacilities[facility] = { urgencies: { 1: 1, 2: 1, 3: 1 } };
        });

        eventsKeys.forEach((eventKey) => {
          const checkedByFacilities = structuredClone(allCheckedByFacilities);

          subscriptions[eventKey] = checkedByFacilities;
        });

        return {
          allFacilitiesChecked,
          subscrLocations: facilities,
          subscriptions,
          columnSubscriptions: { 1: 1, 2: 1, 3: 1 },
          hasSubscriptionsChanges: true,
          childrenIds: [],
        };
      }),
    setSubscrLocations: (subscrLocs, parentsLocIds, node) =>
      set(({ subscriptions, eventsKeys }) => {
        const childrenIds = getAllChildrenIds(node);
        const updatedSubscriptions = structuredClone(subscriptions);
        const hasSubscriptions = updatedSubscriptions[eventsKeys[0]]?.[subscrLocs[0]]?.urgencies;
        let parentsSubscriptions =
          !hasSubscriptions && getParentSubscriptions(parentsLocIds, eventsKeys, updatedSubscriptions);
        const columnSubscriptions = { 1: 1, 2: 1, 3: 1 };
        const currentLocId = subscrLocs[0];

        eventsKeys.forEach((eventKey) => {
          const urgencies = updatedSubscriptions[eventKey]?.[currentLocId]?.urgencies ||
            parentsSubscriptions?.[eventKey] || { 1: 1, 2: 1, 3: 1 };

          if (!updatedSubscriptions[eventKey]?.[currentLocId]) {
            updatedSubscriptions[eventKey] = {
              ...updatedSubscriptions[eventKey],
              [currentLocId]: { urgencies: urgencies },
            };
          } else {
            updatedSubscriptions[eventKey][currentLocId].urgencies = urgencies;
          }

          if (!updatedSubscriptions[eventKey][currentLocId].urgencies[1]) {
            columnSubscriptions[1] = 0;
          }
          if (!updatedSubscriptions[eventKey][currentLocId].urgencies[2]) {
            columnSubscriptions[2] = 0;
          }
          if (!updatedSubscriptions[eventKey][currentLocId].urgencies[3]) {
            columnSubscriptions[3] = 0;
          }
        });

        return {
          subscriptions: updatedSubscriptions,
          subscrLocations: subscrLocs,
          columnSubscriptions,
          childrenIds,
        };
      }),
    setColumnSubscriptions: ({ checked, urgencyKey }) =>
      set(({ subscriptions, columnSubscriptions, eventsKeys, subscrLocations, childrenIds }) => {
        const updatedSubscriptions = structuredClone(subscriptions);

        columnSubscriptions[urgencyKey] = +checked;

        eventsKeys.forEach((eventKey) => {
          subscrLocations.forEach((loc) => {
            updatedSubscriptions[eventKey][loc].urgencies[urgencyKey] = +checked;
          });
          childrenIds.forEach((childId) => {
            delete updatedSubscriptions[eventKey][childId];
          });
        });

        return {
          subscriptions: updatedSubscriptions,
          columnSubscriptions: columnSubscriptions,
          hasSubscriptionsChanges: true,
        };
      }),
    setSubscriptions: ({ checked, eventId, urgencyKey }) =>
      set(({ subscriptions, columnSubscriptions, subscrLocations, eventsKeys, childrenIds }) => {
        const updatedSubscriptions = structuredClone(subscriptions);

        subscrLocations.forEach((locId) => {
          updatedSubscriptions[eventId][locId].urgencies[urgencyKey] = checked;
        });

        eventsKeys.forEach((eventKey) => {
          childrenIds.forEach((childId) => {
            delete updatedSubscriptions[eventKey][childId];
          });
        });

        const colValue = checkColumnValue(
          checked,
          subscrLocations[0],
          urgencyKey,
          updatedSubscriptions,
          eventsKeys,
        );

        columnSubscriptions[urgencyKey] = colValue;

        return {
          subscriptions: updatedSubscriptions,
          columnSubscriptions,
          hasSubscriptionsChanges: true,
        };
      }),
  },
}));

export const useSubscriptionsActions = () => useSubscriptionsStore((state) => state.actions);

export const selectSubscrLocations = () => useSubscriptionsStore((state) => state.subscrLocations);
export const selectSubscriptions = () => useSubscriptionsStore((state) => state.subscriptions);
export const selectColumnSubscriptions = () => useSubscriptionsStore((state) => state.columnSubscriptions);
export const selectEvents = () => useSubscriptionsStore((state) => state.events);
export const selectEventsKeys = () => useSubscriptionsStore((state) => state.eventsKeys);
export const selectHasSubscriptionsChanges = () =>
  useSubscriptionsStore((state) => state.hasSubscriptionsChanges);
export const selectAllFacilitiesChecked = () => useSubscriptionsStore((state) => state.allFacilitiesChecked);
